import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Kalinga Crafts Store';
const SITE_URL = 'https://kalingastore.shop';
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

/**
 * SEO Component for managing head tags
 * International-first: No country-specific signals
 */
const SEO = ({
    title,
    description,
    canonical,
    image = DEFAULT_IMAGE,
    type = 'website',
    product = null,
    breadcrumbs = null,
    noIndex = false,
}) => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : SITE_URL);

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonicalUrl} />
            {noIndex && <meta name="robots" content="noindex, nofollow" />}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content={SITE_NAME} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* Product JSON-LD (currency-ready structure) */}
            {product && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'Product',
                        name: product.name,
                        description: product.description,
                        image: product.images || [product.image],
                        brand: {
                            '@type': 'Brand',
                            name: product.brand || SITE_NAME,
                        },
                        offers: {
                            '@type': 'Offer',
                            priceCurrency: 'INR',
                            price: product.price,
                            availability: product.inStock !== false
                                ? 'https://schema.org/InStock'
                                : 'https://schema.org/OutOfStock',
                            url: canonicalUrl,
                            seller: {
                                '@type': 'Organization',
                                name: product.seller || SITE_NAME,
                            },
                        },
                        ...(product.rating && {
                            aggregateRating: {
                                '@type': 'AggregateRating',
                                ratingValue: product.rating,
                                reviewCount: product.reviews || 1,
                            },
                        }),
                    })}
                </script>
            )}

            {/* Breadcrumb JSON-LD */}
            {breadcrumbs && breadcrumbs.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: breadcrumbs.map((crumb, index) => ({
                            '@type': 'ListItem',
                            position: index + 1,
                            name: crumb.name,
                            item: crumb.url,
                        })),
                    })}
                </script>
            )}
        </Helmet>
    );
};

/**
 * Organization Schema - Add once at App level
 */
export const OrganizationSchema = () => (
    <Helmet>
        <script type="application/ld+json">
            {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Organization',
                name: SITE_NAME,
                url: SITE_URL,
                logo: `${SITE_URL}/logo.png`,
                sameAs: [
                    'https://instagram.com/kalingacrafts',
                    'https://facebook.com/kalingacrafts',
                ],
                contactPoint: {
                    '@type': 'ContactPoint',
                    telephone: '+91-7217786935',
                    contactType: 'customer service',
                },
            })}
        </script>
    </Helmet>
);

export default SEO;
